import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';

/**
 * Minimal accessible modal primitive.
 *
 * Responsibilities:
 *   - Render into a fixed, centered overlay above the main canvas.
 *   - Close on Escape and on backdrop click (if `closeOnBackdrop` is true).
 *   - Trap focus inside the panel — first focusable element on mount.
 *   - Expose the dialog semantics (`role="dialog"`, `aria-modal`,
 *     `aria-labelledby`).
 *
 * Intentionally unopinionated about content (no header/body/footer
 * layout built in). Callers compose their own inner structure using
 * design-system tokens.
 */
interface Props {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly titleId: string;
  readonly children: ReactNode;
  readonly closeOnBackdrop?: boolean;
  readonly testId?: string;
}

export function Modal({
  isOpen,
  onClose,
  titleId,
  children,
  closeOnBackdrop = true,
  testId,
}: Props): JSX.Element | null {
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    function handleKey(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab') {
        return;
      }
      const panel = panelRef.current;
      if (panel === null) {
        return;
      }
      const focusable = panel.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) {
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (first === undefined || last === undefined) {
        return;
      }
      const active = document.activeElement;
      if (event.shiftKey) {
        if (active === first || !panel.contains(active)) {
          event.preventDefault();
          last.focus();
        }
        return;
      }
      if (active === last || !panel.contains(active)) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('keydown', handleKey);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const panel = panelRef.current;
    if (panel === null) {
      return;
    }
    const first = panel.querySelector<HTMLElement>(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    if (first !== null) {
      first.focus();
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      role="presentation"
      data-testid={testId}
      className="fixed inset-0 z-50 flex items-center justify-center bg-surface-container-lowest/80 px-4"
      onMouseDown={(e) => {
        if (closeOnBackdrop && e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="w-full max-w-lg rounded-xl bg-surface-container-highest text-on-surface shadow-[0_12px_32px_rgba(173,198,255,0.04)]"
      >
        {children}
      </div>
    </div>
  );
}
